import {
  ClipAssetResponse,
  assetsAssetV2List,
  assetsClipCreate,
  assetsClipDestroy,
  assetsClipResetFullLengthClipCreate,
  assetsClipRetrieve,
  assetsClipUpdate,
  assetsClipEditCaptionsCreate
} from '@goldcast/api/content';
import { CaptionLineConfig, CaptionStyle, Clip, ClipDeletes, ClipEdits, SourceType } from '@/domains/asset';
import { core, isAudioContent } from '@/stores/core';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { getEnvConfig } from '@/constants';
import { computeCaptionsWithData } from '@/App/remotion/libs/caption/caption';
import { Scenes } from '@/App/remotion/MainPlayer/types/Scene';

const DEFAULT_AUDIO_CONTENT_MAGIC_LAYOUT = {
  // These should be default colors for custom upload audiogram
  backgroundColor: '#FF4200',
  backgroundImage: `${getEnvConfig(
    'CLOUDFRONT_STATIC_ASSET_URL'
  )}content-lab/filestack/brand_kit/templates/background/background-1.png`,
  textColor: '#ffffff'
};

const DEFAULT_MAGIC_LAYOUT = {
  backgroundColor: '#324bff',
  textColor: '#ffffff'
};

export function isCustomUpload(): boolean {
  const content = core.getSnapshot().content;
  return content?.media_source_type === 'UPLOAD';
}

// TODO @@HiteshChoudhary0098 this needs to be updated to handle splits
export function createContentClip(
  start: number,
  end: number,
  source: SourceType,
  deletes: ClipDeletes = {},
  edits: ClipEdits = {},
  scenes?: Scenes,
  fps?: number,
  additionalParams?: Record<string, any>
): Promise<ClipAssetResponse> {
  const content = core.getSnapshot().content;
  const isAudio = isAudioContent();
  const defaultLineConfig = CaptionLineConfig.OneLine;

  const startTimeAdjustedWithDeletes =
    Object.values(deletes).find(del => del.bounds[0] < start && del.bounds[1] > start)?.bounds[1] || start;

  const endTimeAdjustedWithDeletes =
    Object.values(deletes).find(del => del.bounds[0] < end && del.bounds[1] > end)?.bounds[0] || end;
  let updatedScenes: Scenes | undefined = undefined;
  if (fps && scenes) {
    const startTimeAdjustedWithDeletesFPS = startTimeAdjustedWithDeletes * fps;
    const endTimeAdjustedWithDeletesFPS = endTimeAdjustedWithDeletes * fps;
    updatedScenes = scenes
      .filter(
        scene => scene.toFrame >= startTimeAdjustedWithDeletesFPS && scene.fromFrame <= endTimeAdjustedWithDeletesFPS
      )
      .map(scene => {
        return {
          ...scene,
          fromFrame: Math.max(scene.fromFrame, startTimeAdjustedWithDeletesFPS),
          toFrame: Math.min(scene.toFrame, endTimeAdjustedWithDeletesFPS)
        };
      });
  }

  const { captions, duration, excludes } = computeCaptionsWithData({
    start: startTimeAdjustedWithDeletes,
    end: endTimeAdjustedWithDeletes,
    edits,
    deletes,
    merges: [],
    isTranslated: false
  });

  return assetsClipCreate({
    body: {
      content_id: content?.id as string,
      organization: content?.organization as string,
      asset_metadata: {
        start: startTimeAdjustedWithDeletes,
        end: endTimeAdjustedWithDeletes,
        size: 'LANDSCAPE',
        layout: isAudio ? 'AUDIOGRAM' : 'DEFAULT',
        source,
        deletes,
        edits,
        scenes: updatedScenes,
        highlight_type: 'text',
        subtitle: true,
        word_highlight_color: isAudio ? '#11184F' : '#330f80',
        magicLayout: isAudio ? DEFAULT_AUDIO_CONTENT_MAGIC_LAYOUT : DEFAULT_MAGIC_LAYOUT,
        is_edited: false,
        hide_borders: true,
        caption_styles: {
          scale: 1,
          animation: CaptionStyle.Outline,
          line_config: defaultLineConfig
        },
        captions,
        duration,
        excludes,
        ...additionalParams
      }
    }
  });
}

export function deleteContentClip(clipId: string): Promise<any> {
  return assetsClipDestroy({ id: clipId });
}

export function loadClip(id: string, extraQueryParams = ''): Promise<any> {
  let queryParams = '?speakers=true';

  if (extraQueryParams) {
    queryParams += extraQueryParams;
  }

  queryParams += '&block_speaker_layouts=false';

  return assetsClipRetrieve({ id: id + queryParams }).then((clip: ClipAssetResponse) => {
    const isAudio = isAudioContent();
    return {
      ...clip,
      id: clip.id,
      isLoading: false,
      event: clip.content!.project.id,
      broadcast: clip.content!.id,
      organization: clip.asset_metadata.organization,
      title: clip.title,
      broadcast_data: {
        title: clip.content!.title
      },
      content: clip.content,
      asset_metadata: {
        ...(isCustomUpload() && { magicLayout: isAudio ? DEFAULT_AUDIO_CONTENT_MAGIC_LAYOUT : DEFAULT_MAGIC_LAYOUT }),
        ...clip.asset_metadata
      },
      rating: clip.rating,
      isRemoved: false,
      layout_status: clip.layout_status,
      shared: clip.shared,
      locked: clip?.locked,
      updated_at: clip.updated_at,
      first_frame_analysis: clip.first_frame_analysis,
      saved_search: clip.saved_search
    };
  });
}

function transformClipResponse(clip: ClipAssetResponse): any {
  const isAudio = isAudioContent();
  return {
    ...(clip ?? {}),
    id: clip.id,
    event: clip.content!.project.id,
    broadcast: clip.content!.id,
    organization: clip.asset_metadata.organization,
    title: clip.title,
    broadcast_data: {
      title: clip.content!.title
    },
    asset_metadata: {
      ...(isCustomUpload() && { magicLayout: isAudio ? DEFAULT_AUDIO_CONTENT_MAGIC_LAYOUT : DEFAULT_MAGIC_LAYOUT }),
      ...clip.asset_metadata
    },
    rating: clip.rating,
    isRemoved: false,
    layout_status: clip.layout_status,
    shared: clip.shared,
    updated_at: clip.updated_at
  };
}

export function updateClip(clip: Clip): Promise<any> {
  const modifiedClip = { ...clip, asset_metadata: { ...clip.asset_metadata } };
  const shouldDeleteConfig = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Delete_Config];

  if (shouldDeleteConfig) {
    modifiedClip.asset_metadata.config = null;
  }

  return assetsClipUpdate({
    id: modifiedClip.id,
    body: modifiedClip
  }).then(transformClipResponse);
}

export function updateTranslatedCaption(clip: Clip): Promise<any> {
  return assetsClipEditCaptionsCreate({
    body: {
      asset_id: clip.id,
      target_language: clip?.asset_metadata?.selected_lang ?? 'en',
      translation_metadata: {
        edits: clip.asset_metadata.edits,
        merges: clip.asset_metadata?.merges ?? []
      }
    }
  }).then(transformClipResponse);
}

export function resetFullLengthClip(clipId: string): Promise<any> {
  return assetsClipResetFullLengthClipCreate({
    id: clipId,
    body: {}
  });
}

export function loadClips(broadcastId: string): Promise<Clip[]> {
  return assetsAssetV2List({
    queryParams: {
      contents: broadcastId,
      types: 'CLIP',
      limit: 1000,
      ordering: 'rank,-created_at'
    }
  }).then(({ results }) => results as unknown as Clip[]);
}
